import { contrast, readableOn, rimOf } from './color.js';
import { badgeVersion, type BadgeSpec, type Emblem, type Pattern, type Shape } from './spec.js';

/** All drawing happens in a 100 × 120 box. */
const VB_W = 100;
const VB_H = 120;

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** Outline of each shape, as an SVG element without paint attributes. */
const SHAPE_EL: Record<Shape, string> = {
  shield: '<path d="M10 8H90V60C90 89 72 104 50 114C28 104 10 89 10 60Z"/>',
  heater: '<path d="M8 9H92V48Q92 93 50 114Q8 93 8 48Z"/>',
  pointed: '<path d="M10 9L50 3L90 9V64Q90 97 50 116Q10 97 10 64Z"/>',
  round: '<circle cx="50" cy="60" r="46"/>',
  hex: '<polygon points="50,8 92,32 92,88 50,112 8,88 8,32"/>',
  diamond: '<polygon points="50,4 95,60 50,116 5,60"/>',
  banner: '<path d="M12 6H88V100L50 116L12 100Z"/>',
  square: '<rect x="9" y="12" width="82" height="96" rx="15"/>',
};

interface Layout {
  medallion: { cx: number; cy: number; r: number };
  plate: { x: number; y: number; w: number; h: number };
  big: { cx: number; cy: number; r: number };
}

const DEFAULT_LAYOUT: Layout = {
  medallion: { cx: 50, cy: 38, r: 15.5 },
  plate: { x: 19, y: 62, w: 62, h: 24 },
  big: { cx: 50, cy: 58, r: 25 },
};
const LAYOUT: Partial<Record<Shape, Layout>> = {
  round: { medallion: { cx: 50, cy: 38, r: 15 }, plate: { x: 20, y: 62, w: 60, h: 23 }, big: { cx: 50, cy: 60, r: 26 } },
  diamond: { medallion: { cx: 50, cy: 42, r: 13 }, plate: { x: 25, y: 61, w: 50, h: 22 }, big: { cx: 50, cy: 60, r: 23 } },
  banner: { medallion: { cx: 50, cy: 36, r: 15.5 }, plate: { x: 19, y: 60, w: 62, h: 24 }, big: { cx: 50, cy: 56, r: 26 } },
  square: { medallion: { cx: 50, cy: 40, r: 15.5 }, plate: { x: 19, y: 64, w: 62, h: 24 }, big: { cx: 50, cy: 60, r: 27 } },
};

function patternEls(p: Pattern, c: string): string {
  const f = `fill="${c}"`;
  switch (p) {
    case 'plain':
      return '';
    case 'stripes': {
      let s = '';
      for (let x = 100 / 7; x < 100; x += (2 * 100) / 7) s += `<rect x="${x.toFixed(2)}" y="0" width="${(100 / 7).toFixed(2)}" height="${VB_H}" ${f}/>`;
      return s;
    }
    case 'pinstripes': {
      let s = '';
      for (let x = 8; x < 100; x += 9) s += `<rect x="${x}" y="0" width="2.2" height="${VB_H}" ${f}/>`;
      return s;
    }
    case 'hoops': {
      let s = '';
      for (let y = 15; y < VB_H; y += 30) s += `<rect x="0" y="${y}" width="${VB_W}" height="15" ${f}/>`;
      return s;
    }
    case 'halves':
      return `<rect x="50" y="0" width="50" height="${VB_H}" ${f}/>`;
    case 'quarters':
      return `<rect x="50" y="0" width="50" height="60" ${f}/><rect x="0" y="60" width="50" height="60" ${f}/>`;
    case 'sash':
      return `<polygon points="-12,8 8,-12 112,112 92,132" ${f}/>`;
    case 'chevron':
      return `<polygon points="0,30 50,72 100,30 100,52 50,94 0,52" ${f}/>`;
    case 'cross':
      return `<rect x="41" y="0" width="18" height="${VB_H}" ${f}/><rect x="0" y="51" width="${VB_W}" height="18" ${f}/>`;
    case 'checks': {
      let s = '';
      for (let y = 0; y < VB_H; y += 15) for (let x = (y / 15) % 2 === 0 ? 0 : 15; x < VB_W; x += 30) s += `<rect x="${x}" y="${y}" width="15" height="15" ${f}/>`;
      return s;
    }
    case 'band':
      return `<rect x="0" y="48" width="${VB_W}" height="24" ${f}/>`;
    case 'border':
      return ''; // drawn as an inner outline instead (see below)
  }
}

function star(cx: number, cy: number, r: number, inner = 0.42): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const rad = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * inner;
    pts.push(`${(cx + rr * Math.cos(rad)).toFixed(2)},${(cy + rr * Math.sin(rad)).toFixed(2)}`);
  }
  return `<polygon points="${pts.join(' ')}"/>`;
}

/** Emblems drawn around (0,0) within roughly ±13 units. `c` = emblem colour, `bg` = colour behind it. */
function emblemEls(e: Emblem, c: string, bg: string): string {
  switch (e) {
    case 'none':
      return '';
    case 'star':
      return `<g fill="${c}">${star(0, 1, 13)}</g>`;
    case 'stars3':
      return `<g fill="${c}">${star(-9, 4, 5.5)}${star(0, -5, 6.5)}${star(9, 4, 5.5)}</g>`;
    case 'ball': {
      // The ball is white; its panels need a colour that shows on white.
      const d = readableOn('#FFFFFF', [c, bg], 3);
      const pent: string[] = [];
      const spokes: string[] = [];
      for (let i = 0; i < 5; i++) {
        const a = (2 * Math.PI * i) / 5 - Math.PI / 2;
        const x = 4.6 * Math.cos(a);
        const y = 4.6 * Math.sin(a);
        pent.push(`${x.toFixed(2)},${y.toFixed(2)}`);
        spokes.push(`M${x.toFixed(2)} ${y.toFixed(2)}L${(11.6 * Math.cos(a)).toFixed(2)} ${(11.6 * Math.sin(a)).toFixed(2)}`);
      }
      return `<circle r="12.4" fill="#FFFFFF" stroke="${d}" stroke-width="1.6"/><polygon points="${pent.join(' ')}" fill="${d}"/><path d="${spokes.join('')}" stroke="${d}" stroke-width="1.5"/>`;
    }
    case 'crown':
      return `<g fill="${c}"><path d="M-13 6L-13-6L-6.5 0L0-10L6.5 0L13-6L13 6Z"/><rect x="-13" y="8" width="26" height="3.6"/></g>`;
    case 'bolt':
      return `<path fill="${c}" d="M3-13L-8.5 2H-1L-4 13L8.5-3H1Z"/>`;
    case 'wave':
      return `<g fill="none" stroke="${c}" stroke-width="3" stroke-linecap="round"><path d="M-13-4q3.25-5 6.5 0t6.5 0t6.5 0t6.5 0"/><path d="M-13 5q3.25-5 6.5 0t6.5 0t6.5 0t6.5 0"/></g>`;
    case 'tower':
      return `<path fill="${c}" d="M-9 13V-6H-10.5V-12H-6V-8.5H-2V-12H2V-8.5H6V-12H10.5V-6H9V13Z"/><path fill="${bg}" d="M-3 13V6.5A3 3 0 0 1 3 6.5V13Z"/>`;
    case 'oak':
      return `<g fill="${c}"><circle cx="-5.5" cy="-2" r="6.5"/><circle cx="5.5" cy="-2" r="6.5"/><circle cx="0" cy="-8.5" r="6.5"/><rect x="-1.8" y="2" width="3.6" height="11"/></g>`;
    case 'anchor':
      return `<g fill="none" stroke="${c}" stroke-width="2.6" stroke-linecap="round"><circle cy="-9.5" r="3"/><path d="M0-6.5V12M-7-2H7M-11.5 3Q-10.5 12 0 12Q10.5 12 11.5 3"/></g>`;
    case 'wings':
      return `<g fill="${c}"><path d="M-1.5 3C-6-2-10-4-14-10C-12-2-9 4-1.5 8Z"/><path d="M-1.5 9C-6 6-10 4-13-1C-10 7-6 10-1.5 12Z"/><g transform="scale(-1 1)"><path d="M-1.5 3C-6-2-10-4-14-10C-12-2-9 4-1.5 8Z"/><path d="M-1.5 9C-6 6-10 4-13-1C-10 7-6 10-1.5 12Z"/></g></g>`;
    case 'trophy':
      return `<g fill="${c}"><path d="M-8-12H8V-4A8 8 0 0 1-8-4Z"/><rect x="-1.6" y="3.5" width="3.2" height="5"/><rect x="-7" y="8.5" width="14" height="4" rx="1"/></g><g fill="none" stroke="${c}" stroke-width="2"><path d="M-8-10H-12V-7.5A4.5 4.5 0 0 0-7.5-3"/><path d="M8-10H12V-7.5A4.5 4.5 0 0 1 7.5-3"/></g>`;
    case 'ring':
      return `<circle r="10" fill="none" stroke="${c}" stroke-width="3.5"/><circle r="3.6" fill="${c}"/>`;
    case 'arrow':
      return `<g fill="none" stroke="${c}" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"><path d="M-11 3L0-8L11 3"/><path d="M-11 12L0 1L11 12"/></g>`;
  }
}

/** Approximate advance width of condensed bold capitals, as a fraction of font size. */
const CHAR_W = 0.62;

function textEl(text: string, cx: number, cy: number, size: number, maxW: number, fill: string): string {
  const w = Math.min(maxW, text.length * size * CHAR_W);
  return (
    `<text x="${cx}" y="${cy}" fill="${fill}" font-family="'Barlow Condensed','Roboto Condensed','Arial Narrow',Arial,sans-serif" ` +
    `font-weight="800" font-size="${size}" text-anchor="middle" dominant-baseline="central" ` +
    `textLength="${w.toFixed(1)}" lengthAdjust="spacingAndGlyphs">${esc(text)}</text>`
  );
}

export interface RenderOptions {
  /** Rendered width in px; height follows the 5:6 box. Omit for a fluid SVG. */
  size?: number;
  /** Accessible name. Omit for a decorative badge (`aria-hidden`). */
  title?: string;
  /** Prefix for internal ids; set it when several badges are inlined in one HTML page. */
  idPrefix?: string;
}

/** The colours actually used, after contrast checks (exported for tests). */
export function paintOf(spec: BadgeSpec) {
  const rim = rimOf(spec.primary);
  const trim = contrast(spec.secondary, spec.primary) >= 1.5 ? spec.secondary : rim;
  const emblem = readableOn(spec.primary, [spec.accent, spec.secondary], 3);
  const text = readableOn(spec.primary, [spec.secondary, spec.accent], 4.5);
  return { field: spec.primary, rim, trim, emblem, text, plate: spec.primary };
}

export function renderBadgeSvg(spec: BadgeSpec, opts: RenderOptions = {}): string {
  const id = `${opts.idPrefix ?? 'bdg'}-${badgeVersion(spec)}`;
  const paint = paintOf(spec);
  const lay = LAYOUT[spec.shape] ?? DEFAULT_LAYOUT;
  const shape = SHAPE_EL[spec.shape];
  const withAttrs = (el: string, attrs: string) => el.replace(/\/>$/, ` ${attrs}/>`);

  const parts: string[] = [];
  parts.push(`<defs><clipPath id="${id}-clip">${shape}</clipPath></defs>`);
  parts.push(withAttrs(shape, `fill="${paint.field}"`));
  parts.push(`<g clip-path="url(#${id}-clip)">${patternEls(spec.pattern, spec.secondary)}</g>`);
  if (spec.pattern === 'border') {
    // Inner outline: the shape scaled about its centre.
    parts.push(
      `<g transform="translate(50 60) scale(0.86) translate(-50 -60)">${withAttrs(shape, `fill="none" stroke="${paint.trim}" stroke-width="3.5"`)}</g>`,
    );
  }
  parts.push(withAttrs(shape, `fill="none" stroke="${paint.rim}" stroke-width="4" stroke-linejoin="round"`));

  const layout = spec.emblem === 'none' && spec.layout !== 'monogram' ? 'monogram' : spec.layout;
  if (layout === 'stack') {
    const m = lay.medallion;
    const p = lay.plate;
    const scale = m.r / 15.5;
    parts.push(`<circle cx="${m.cx}" cy="${m.cy}" r="${m.r}" fill="${paint.field}" stroke="${paint.trim}" stroke-width="2"/>`);
    parts.push(`<g transform="translate(${m.cx} ${m.cy}) scale(${scale.toFixed(3)})">${emblemEls(spec.emblem, paint.emblem, paint.field)}</g>`);
    parts.push(`<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="5" fill="${paint.plate}" stroke="${paint.trim}" stroke-width="1.8"/>`);
    parts.push(textEl(spec.text, 50, p.y + p.h / 2 + 0.5, p.h * 0.78, p.w - 10, paint.text));
  } else if (layout === 'emblem') {
    const b = lay.big;
    parts.push(`<circle cx="${b.cx}" cy="${b.cy}" r="${b.r}" fill="${paint.field}" stroke="${paint.trim}" stroke-width="2.4"/>`);
    parts.push(`<g transform="translate(${b.cx} ${b.cy}) scale(${(b.r / 15).toFixed(3)})">${emblemEls(spec.emblem, paint.emblem, paint.field)}</g>`);
  } else {
    const b = lay.big;
    parts.push(`<circle cx="${b.cx}" cy="${b.cy}" r="${b.r}" fill="${paint.field}" stroke="${paint.trim}" stroke-width="2.4"/>`);
    parts.push(textEl(spec.text, b.cx, b.cy + 1, b.r * (spec.text.length >= 3 ? 0.78 : 0.95), b.r * 1.62, paint.text));
  }

  const size = opts.size ? ` width="${opts.size}" height="${Math.round((opts.size * VB_H) / VB_W)}"` : '';
  const a11y = opts.title ? ` role="img" aria-label="${esc(opts.title)}"` : ' aria-hidden="true"';
  const title = opts.title ? `<title>${esc(opts.title)}</title>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_W} ${VB_H}"${size}${a11y}>${title}${parts.join('')}</svg>`;
}

/** For `<img src>` without a network request. */
export function svgDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
